import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { db } from '../../firebase/firebase';
import { doc, getDoc } from 'firebase/firestore';
import {
  Box,
  Typography,
  TextField,
  Paper,
  Container,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  InputAdornment,
  IconButton,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  TableBar as TableIcon,
  Person as PersonIcon,
  Home as HomeIcon
} from '@mui/icons-material';

const QrCodeScanner = () => {
  const { t } = useTranslation();
  const { weddingId } = useParams();
  
  const [wedding, setWedding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    const fetchWedding = async () => {
      try {
        // Direct Firestore access without requiring authentication
        const weddingRef = doc(db, 'weddings', weddingId);
        const weddingDoc = await getDoc(weddingRef);
        
        if (!weddingDoc.exists()) {
          setError(t('common.notFound'));
          return;
        }
        
        const weddingData = { id: weddingDoc.id, ...weddingDoc.data() };
        setWedding(weddingData);
        
        // Store in localStorage for offline access
        localStorage.setItem(`wedding_${weddingId}`, JSON.stringify(weddingData));
      } catch (err) {
        console.error('Error fetching wedding:', err);
        
        // Try to load from localStorage if network request fails
        const cachedWedding = localStorage.getItem(`wedding_${weddingId}`);
        if (cachedWedding) {
          try {
            setWedding(JSON.parse(cachedWedding));
          } catch (parseErr) {
            console.error('Error parsing cached wedding data:', parseErr);
            setError(t('common.error'));
          }
        } else {
          setError(t('common.error'));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchWedding();
  }, [weddingId, t]);

  const handleSearch = () => {
    if (!searchQuery.trim() || !wedding) {
      setSearchResults([]);
      setHasSearched(false);
      return;
    }
    
    setHasSearched(true);
    
    // Search for the name in all tables
    const results = [];
    
    wedding.tables?.forEach(table => {
      table.members?.forEach(member => {
        if (member.name.toLowerCase().includes(searchQuery.toLowerCase())) {
          results.push({
            name: member.name,
            tableNumber: table.tableNumber,
            tableId: table.id
          });
        }
      });
    });
    
    setSearchResults(results);
  };

  const handleClear = () => {
    setSearchQuery('');
    setSearchResults([]);
    setHasSearched(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!wedding) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Alert severity="error">{t('common.notFound')}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {wedding.name}
          </Typography>
          <Typography variant="subtitle1">
            {t('qrCode.findTable')}
          </Typography>
        </Box>
        
        <Divider sx={{ mb: 3 }} />
        
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label={t('qrCode.searchPlaceholder')}
            variant="outlined"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {searchQuery && (
                    <IconButton onClick={handleClear} edge="end">
                      <ClearIcon />
                    </IconButton>
                  )}
                  <IconButton onClick={handleSearch} edge="end">
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        
        {hasSearched && (
          <Box>
            {searchResults.length > 0 ? (
              <List>
                {searchResults.map((result, index) => (
                  <ListItem key={index} component={Paper} sx={{ mb: 2, p: 2 }}>
                    <ListItemIcon>
                      <PersonIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={result.name}
                      secondary={
                        <Typography variant="body1" color="text.secondary">
                          {t('qrCode.yourTable')}: <strong>{result.tableNumber}</strong>
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Alert severity="info">{t('qrCode.notFound')}</Alert>
            )}
          </Box>
        )}
      </Paper>
      
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          {t('tables.title')}
        </Typography>
        
        <Grid container spacing={3}>
          {wedding.tables?.map((table) => (
            <Grid item xs={12} sm={6} md={4} key={table.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TableIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" component="div">
                      {t('tables.tableNumber')}: {table.tableNumber}
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ mb: 2 }} />
                  
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {t('members.title')} ({table.members?.length || 0})
                  </Typography>
                  
                  {table.members?.length > 0 ? (
                    <List dense>
                      {table.members?.map((member) => (
                        <ListItem key={member.id} disableGutters>
                          <PersonIcon sx={{ mr: 1, fontSize: 16 }} />
                          <ListItemText primary={member.name} />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t('tables.noMembers')}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Container>
  );
};

export default QrCodeScanner;
